import { ColumnType } from '@thoughtspot/ts-chart-sdk';
import { ChartType, getAxisEncodingKeysForType } from './chart-type';
import { getAllDimensionCols, getAxisDrivenSlotColumns, getMNField } from './helpers';
import { addMeasureValueField } from './measure-name-value';
import { buildColumnTotalsQuery, buildRowTotalsQuery, shouldInjectOppositeTotals, } from './totals-queries';
import { getFieldsByEncoding, } from './fields';
import { ALL_TOTALS, DataQueryBucketKey, NO_TOTALS, } from './types';
function overridesFromSummariesState(state) {
    return {
        columnGrandTotalsEnabled: state?.showColumnGrandTotals,
        isColSubTotalsEnabled: state?.showColumnTotals,
        isRowGrandTotalsEnabled: state?.showRowGrandTotals,
        isRowSubTotalsEnabled: state?.showRowTotals,
    };
}
function applyFlagOverrides(saved, overrides) {
    const merge = (override, savedValue) => override === undefined ? savedValue : override ?? false;
    return {
        columnGrandTotalsEnabled: merge(overrides.columnGrandTotalsEnabled, saved.columnGrandTotalsEnabled),
        isColSubTotalsEnabled: merge(overrides.isColSubTotalsEnabled, saved.isColSubTotalsEnabled),
        isRowGrandTotalsEnabled: merge(overrides.isRowGrandTotalsEnabled, saved.isRowGrandTotalsEnabled),
        isRowSubTotalsEnabled: merge(overrides.isRowSubTotalsEnabled, saved.isRowSubTotalsEnabled),
    };
}
function flagsFromNestedTotalsSummary(summary) {
    const grand = summary['show-grand-totals'];
    const sub = summary['show-sub-totals'];
    return {
        columnGrandTotalsEnabled: grand?.['show-column-grand-totals'] ?? false,
        isRowGrandTotalsEnabled: grand?.['show-row-grand-totals'] ?? false,
        isColSubTotalsEnabled: sub?.['show-column-sub-totals'] ?? false,
        isRowSubTotalsEnabled: sub?.['show-row-sub-totals'] ?? false,
    };
}
function flagsFromFlatTotalsSummary(summary) {
    return {
        columnGrandTotalsEnabled: summary['show-column-grand-totals'] ?? false,
        isRowGrandTotalsEnabled: summary['show-row-grand-totals'] ?? false,
        isColSubTotalsEnabled: summary['show-column-sub-totals'] ?? false,
        isRowSubTotalsEnabled: summary['show-row-sub-totals'] ?? false,
    };
}
function isPivotGrandTotalsDisabled(fields) {
    const dimensionCols = getAllDimensionCols(fields);
    return dimensionCols.length ? dimensionCols.length === 1 && !!getMNField(fields) : true;
}
function isPivotSubTotalsDisabled(fields) {
    return fields.length > 1 ? fields.length === 2 && !!getMNField(fields) : true;
}
function clampPivotStoredFlags(flags, chartConfig) {
    const { rowFields, colFields } = getFieldsByEncoding(chartConfig, getAxisEncodingKeysForType(ChartType.Pivot));
    return {
        columnGrandTotalsEnabled: flags.columnGrandTotalsEnabled && !isPivotGrandTotalsDisabled(rowFields),
        isColSubTotalsEnabled: flags.isColSubTotalsEnabled && !isPivotSubTotalsDisabled(rowFields),
        isRowGrandTotalsEnabled: flags.isRowGrandTotalsEnabled && !isPivotGrandTotalsDisabled(colFields),
        isRowSubTotalsEnabled: flags.isRowSubTotalsEnabled && !isPivotSubTotalsDisabled(colFields),
    };
}
export function getTotalsEnabledFlags(inputs, chartType, chartConfig) {
    const overrides = overridesFromSummariesState(inputs.summariesState);
    const hasOverride = Object.values(overrides).some((v) => v !== undefined);
    if (chartType === ChartType.Pivot) {
        if (!inputs.totalsSummary && !hasOverride) {
            return inputs.pivotTotalsOnByDefault ? ALL_TOTALS : NO_TOTALS;
        }
        const saved = inputs.totalsSummary
            ? flagsFromNestedTotalsSummary(inputs.totalsSummary)
            : NO_TOTALS;
        const merged = applyFlagOverrides(saved, overrides);
        return chartConfig ? clampPivotStoredFlags(merged, chartConfig) : merged;
    }
    const saved = inputs.totalsSummary
        ? flagsFromFlatTotalsSummary(inputs.totalsSummary)
        : NO_TOTALS;
    return applyFlagOverrides(saved, overrides);
}
function isGrandTotalsUnsupported(fields) {
    const attributeCols = fields.filter((c) => c.type === ColumnType.ATTRIBUTE);
    return attributeCols.length ? attributeCols.length === 1 && !!getMNField(fields) : true;
}
function isSubTotalsUnsupported(fields) {
    const attributeCols = fields.filter((c) => c.type === ColumnType.ATTRIBUTE);
    return attributeCols.length > 1 ? attributeCols.length === 2 && !!getMNField(fields) : true;
}
function getSupportedTotalsFlags(chartConfig, chartType) {
    const axisEncodings = getAxisEncodingKeysForType(chartType);
    const rowAxisCols = getAxisDrivenSlotColumns(chartConfig, axisEncodings.rows);
    const columnAxisCols = getAxisDrivenSlotColumns(chartConfig, axisEncodings.columns);
    return {
        columnGrandTotalsEnabled: !isGrandTotalsUnsupported(rowAxisCols),
        isColSubTotalsEnabled: !isSubTotalsUnsupported(rowAxisCols),
        isRowGrandTotalsEnabled: !isGrandTotalsUnsupported(columnAxisCols),
        isRowSubTotalsEnabled: !isSubTotalsUnsupported(columnAxisCols),
    };
}
function intersectTotalsFlags(a, b) {
    return {
        columnGrandTotalsEnabled: a.columnGrandTotalsEnabled && b.columnGrandTotalsEnabled,
        isColSubTotalsEnabled: a.isColSubTotalsEnabled && b.isColSubTotalsEnabled,
        isRowGrandTotalsEnabled: a.isRowGrandTotalsEnabled && b.isRowGrandTotalsEnabled,
        isRowSubTotalsEnabled: a.isRowSubTotalsEnabled && b.isRowSubTotalsEnabled,
    };
}
const SLOT_TRELLIS_BY = 'trellis-by';
function isTrellisEnabled(chartConfig) {
    return getAxisDrivenSlotColumns(chartConfig, SLOT_TRELLIS_BY).length > 0;
}
export function reconcileTotalsFlags(flags, chartConfig, chartType) {
    if (chartType === ChartType.Pivot)
        return flags;
    if (isTrellisEnabled(chartConfig))
        return NO_TOTALS;
    return intersectTotalsFlags(flags, getSupportedTotalsFlags(chartConfig, chartType));
}
export function getTotalsQueries({ chartModel, chartConfig, queryParams, axisEncodings, totalsEnabledFlags, }) {
    const flags = totalsEnabledFlags;
    const queryIndexTypeMap = {
        [DataQueryBucketKey.Root]: null,
        [DataQueryBucketKey.RowGrandTotals]: null,
        [DataQueryBucketKey.ColumnGrandTotals]: null,
        [DataQueryBucketKey.RowSubTotals]: null,
        [DataQueryBucketKey.ColumnSubTotals]: null,
        [DataQueryBucketKey.GreatSubTotals]: null,
        [DataQueryBucketKey.GreatGrandTotals]: null,
        [DataQueryBucketKey.LineRoot]: null,
        [DataQueryBucketKey.LineRowGrandTotals]: null,
        [DataQueryBucketKey.LineRowSubTotals]: null,
    };
    const anyTotalsEnabled = flags.columnGrandTotalsEnabled ||
        flags.isColSubTotalsEnabled ||
        flags.isRowGrandTotalsEnabled ||
        flags.isRowSubTotalsEnabled;
    if (!anyTotalsEnabled) {
        return { queries: [], queryIndexTypeMap };
    }
    const { columnGrandTotals, columnSubTotals, greatGrandTotals, greatSubTotals, } = buildColumnTotalsQuery(chartConfig, queryParams, axisEncodings);
    const { rowGrandTotals, rowSubTotals } = buildRowTotalsQuery(chartConfig, queryParams, axisEncodings);
    const { rowFields, colFields } = getFieldsByEncoding(chartConfig, axisEncodings);
    const injectColGrandInColSub = shouldInjectOppositeTotals(flags.columnGrandTotalsEnabled, rowFields);
    const injectRowGrandInRowSub = shouldInjectOppositeTotals(flags.isRowGrandTotalsEnabled, colFields);
    const buckets = [
        {
            key: DataQueryBucketKey.ColumnGrandTotals,
            enabled: flags.columnGrandTotalsEnabled ||
                (injectColGrandInColSub && flags.isColSubTotalsEnabled),
            sets: columnGrandTotals,
        },
        {
            key: DataQueryBucketKey.ColumnSubTotals,
            enabled: flags.isColSubTotalsEnabled,
            sets: columnSubTotals,
        },
        {
            key: DataQueryBucketKey.RowGrandTotals,
            enabled: flags.isRowGrandTotalsEnabled ||
                (injectRowGrandInRowSub && flags.isRowSubTotalsEnabled),
            sets: rowGrandTotals,
        },
        {
            key: DataQueryBucketKey.RowSubTotals,
            enabled: flags.isRowSubTotalsEnabled,
            sets: rowSubTotals,
        },
        {
            key: DataQueryBucketKey.GreatGrandTotals,
            enabled: (flags.columnGrandTotalsEnabled && flags.isRowGrandTotalsEnabled) ||
                ((injectColGrandInColSub || injectRowGrandInRowSub) &&
                    (flags.isColSubTotalsEnabled || flags.isRowSubTotalsEnabled)),
            sets: greatGrandTotals,
        },
        {
            key: DataQueryBucketKey.GreatSubTotals,
            enabled: flags.isColSubTotalsEnabled && flags.isRowSubTotalsEnabled,
            sets: greatSubTotals,
        },
    ];
    let queries = [];
    for (const { key, enabled, sets } of buckets) {
        if (!enabled || !sets?.length)
            continue;
        const start = queries.length;
        queries = queries.concat(sets);
        queryIndexTypeMap[key] = sets.map((_, i) => start + i);
    }
    queries = addMeasureValueField(queries, chartModel);
    return { queries, queryIndexTypeMap };
}
//# sourceMappingURL=totals.js.map